import * as React from "react";
import { Icon, IconInput, Spinner } from "./Icon";

/**
 * The loading / error / empty triple every list needs, lifted out of ScadsysIdentity's
 * users/UsersList.tsx so the three states look the same everywhere instead of being re-invented per
 * screen. `ListStates` is the one call sites use: it renders whichever state applies and otherwise
 * gets out of the way.
 */

export const LoadingState: React.FC<{ label?: string }> = ({ label = "Loading..." }) => (
    <div className="flex items-center justify-center gap-2 py-12 text-sm text-text-weak" aria-busy="true">
        <Spinner />
        {label}
    </div>
);

export const ErrorState: React.FC<{ message: React.ReactNode; onRetry?: () => void }> = ({ message, onRetry }) => (
    <div className="flex flex-col items-center justify-center gap-2 py-12 text-center" role="alert">
        <Icon icon={["fad", "triangle-exclamation"]} className="text-3xl text-danger" />
        <p className="text-sm text-danger-text">{message}</p>
        {onRetry && (
            <button type="button" className="text-sm font-medium text-primary-text underline" onClick={onRetry}>
                Try again
            </button>
        )}
    </div>
);

export interface EmptyStateProps {
    icon?: IconInput;
    title: string;
    hint?: React.ReactNode;
    action?: React.ReactNode;
}

export const EmptyState: React.FC<EmptyStateProps> = ({ icon = ["fad", "inbox"], title, hint, action }) => (
    <div className="flex flex-col items-center justify-center gap-2 py-12 text-center">
        <Icon icon={icon} className="text-3xl text-text-weak" />
        <h3 className="text-sm font-semibold text-text">{title}</h3>
        {hint && <p className="max-w-md text-sm text-text-weak">{hint}</p>}
        {action && <div className="mt-2">{action}</div>}
    </div>
);

export interface ListStatesProps {
    isLoading: boolean;
    error?: React.ReactNode;
    isEmpty: boolean;
    empty: EmptyStateProps;
    onRetry?: () => void;
    children: React.ReactNode;
}

/**
 * Renders whichever state applies. On a refresh (loading with content already on screen) the
 * existing rows stay visible, dimmed and marked `aria-busy`, rather than flashing back to a spinner.
 */
export const ListStates: React.FC<ListStatesProps> = ({ isLoading, error, isEmpty, empty, onRetry, children }) => {
    if (error) return <ErrorState message={error} onRetry={onRetry} />;
    if (isLoading && isEmpty) return <LoadingState />;
    if (isEmpty) return <EmptyState {...empty} />;

    return (
        <div aria-busy={isLoading || undefined} className={isLoading ? "opacity-60 transition-opacity" : undefined}>
            {children}
        </div>
    );
};

/** Inline message block - the local error banner pattern, named. */
export type BannerTone = "info" | "success" | "warning" | "danger";

const BANNERS: Record<BannerTone, { classes: string; icon: IconInput }> = {
    info: { classes: "bg-info-soft border-info-border text-info-text", icon: ["fad", "circle-info"] },
    success: { classes: "bg-success-soft border-success-border text-success-text", icon: ["fad", "circle-check"] },
    warning: { classes: "bg-warning-soft border-warning-border text-warning-text", icon: ["fad", "triangle-exclamation"] },
    danger: { classes: "bg-danger-soft border-danger-border text-danger-text", icon: ["fad", "circle-exclamation"] },
};

export const Banner: React.FC<{ tone?: BannerTone; children: React.ReactNode; className?: string }> = ({
    tone = "danger",
    children,
    className = "",
}) => {
    const { classes, icon } = BANNERS[tone];

    return (
        <div
            className={`flex items-start gap-2 rounded-card border p-3 text-sm ${classes} ${className}`}
            role={tone === "danger" ? "alert" : undefined}
        >
            <Icon icon={icon} className="mt-0.5" />
            <div className="min-w-0 flex-1">{children}</div>
        </div>
    );
};
